// src/components/RankingBarrasEstudiantes.js
import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const getRoleDisplayName = (shortname) => {
  if (shortname === "student") return "Estudiante";
  if (shortname === "teacher" || shortname === "editingteacher" || shortname === "profesorgenerartarea") return "Profesor";
  return shortname || "Usuario";
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, minutos, role_display, colleges } = payload[0].payload;
    return (
      <div style={{ backgroundColor: "#fff", border: "1px solid #ccc", padding: "10px", maxWidth: "300px" }}>
        <p style={{ margin: "0 0 5px" }}><strong>{name}</strong></p>
        <p style={{ margin: "0 0 5px" }}><strong>Tiempo:</strong> {minutos} min</p>
        <p style={{ margin: "0 0 5px" }}><strong>Rol:</strong> {role_display}</p>
        {colleges && colleges.length > 0 && (
          <p style={{ margin: "0", fontSize: "0.85rem", color: "#555" }}>
            <strong>Colegios:</strong> {colleges.join(", ")}
          </p>
        )}
      </div>
    );
  }
  return null;
};

function RankingBarrasEstudiantes({ data, onRankingDataChange, showTop = true, showBottom = true, title = "Top 10 Estudiantes", labelText = "Estudiante" }) {

  const { top10, bottom10 } = useMemo(() => {
    if (!data || data.length === 0) return { top10: [], bottom10: [] };

    // 1. Agrupar por UserID para evitar duplicados, pero guardando metadatos
    const map = {};

    data.forEach(d => {
      const uid = d.userid;
      const activeSeconds = Number(d.active_seconds) || 0;
      // Identificar colegio: fallback a category_name si no existe institution_name
      const collegeName = d.institution_name || d.category_name || "Desconocido";

      if (!map[uid]) {
        map[uid] = {
          userid: uid,
          name: `${d.user_fname} ${d.user_sname}`,
          totalSeconds: 0,
          role_shortname: d.role_shortname,
          collegesSet: new Set()
        };
      }
      map[uid].totalSeconds += activeSeconds;
      if (collegeName) {
        map[uid].collegesSet.add(collegeName);
      }
    });

    // 2. Convertir a array
    const list = Object.values(map).map(obj => ({
      name: obj.name,
      minutos: Math.round(obj.totalSeconds / 60), // convertir a minutos
      role_display: getRoleDisplayName(obj.role_shortname),
      colleges: Array.from(obj.collegesSet), // Convertir Set a Array para display
      userid: obj.userid
    }));

    // 3. Ordenar descendente
    list.sort((a, b) => b.minutos - a.minutos);

    // 4. Top 10
    const top10 = list.slice(0, 10);

    // 5. Bottom 10 (los que tienen > 0 minutos, tomamos los ultimos 10)
    const activeList = list.filter(x => x.minutos > 0);
    const bottom10 = activeList.slice(-10).reverse();

    return { top10, bottom10 };
  }, [data]);

  // Notificar al padre (si se usa para exportación, etc)
  const finalRanking = useMemo(() => {
    const arr = [];
    if (showTop) {
      arr.push(...top10.map((obj) => ({ ...obj, rankingType: "TOP" })));
    }
    if (showBottom) {
      arr.push(...bottom10.map((obj) => ({ ...obj, rankingType: "BOTTOM" })));
    }
    return arr;
  }, [top10, bottom10, showTop, showBottom]);

  React.useEffect(() => {
    if (onRankingDataChange) {
      onRankingDataChange(finalRanking);
    }
  }, [finalRanking, onRankingDataChange]);

  return (
    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", justifyContent: "center" }}>
      {/* Top 10 */}
      {showTop && (
        <div style={{ flex: 1, minWidth: "300px", maxWidth: "100%" }}>
          <h2 style={{ textAlign: "center", fontSize: "1.2rem", color: "#003366" }}>{title}</h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={top10}
              layout="vertical"
              margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis
                dataKey="name"
                type="category"
                width={150}
                style={{ fontSize: '0.8rem' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="minutos" fill="#8884d8" radius={[0, 4, 4, 0]} barSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Bottom 10 (Opcional, controlado por prop) */}
      {showBottom && bottom10.length > 0 && (
        <div style={{ flex: 1, minWidth: "300px", maxWidth: "100%" }}>
          <h2 style={{ textAlign: "center", fontSize: "1.2rem", color: "#003366" }}>{title}</h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={bottom10}
              layout="vertical"
              margin={{ top: 20, right: 20, left: 10, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis
                dataKey="name"
                type="category"
                width={150}
                style={{ fontSize: '0.8rem' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="minutos" fill="#82ca9d" radius={[0, 4, 4, 0]} barSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default RankingBarrasEstudiantes;
